import { hasCardData, getCardData } from './../get-card-details';
import { EmeraldError } from 'src/common/api/emerald-error';
import { store } from 'src/common/redux/configure-store';
import { v4 as uuid } from 'uuid';
import { getPinPadConfig, parsePinpadConfig } from '../pinpad-utils';
import { IPrescriptionItem } from 'src/common/interfaces/dtos/IEwic';
import { EntryMethod } from 'src/common/enumerations/entry-method';

// PaymentMessage DTO structure
export interface PaymentMessage {
  amounts?: Amounts;
  cardDetails?: CardDetails[];
  checkDetails?: CheckDetails;
  clientID?: string;
  configOptions?: ConfigOptions;
  deviceData?: DeviceData;
  hostData?: HostData;
  paymentDetails?: PaymentDetails;
  posTranID?: string;
  receipts?: ReceiptData[];
  requestID?: string;
  signatureDetails?: SignatureDetails;
  status?: Status;
  ewicData?: string;
  statusCode?: number;
  statusMessage?: string;
  promptDetails?: PromptDetails;
  trainingMode?: boolean;
  transactionDateTimeUTC?: string;
  expectedCardDetailsContext?: CardDetailsContext;
  nmpMessage?: object;
}

//Electronic Payment Data preserved in our Redux Store
export interface EpsData {
  paymentTranID: string;
  sessionTranID: string;
  card: CardDetails;
  amountDue?: string;
  cashbackAmount?: number;
  amountApproved?: string;
  referenceID?: string;
  authCode?: string;
  ewicEpsPrescriptionData?: EwicEpsPrescriptionData;
}

export interface Amounts {
  activationAmount?: string;
  amountApproved?: string;
  amountDue?: string;
  amountTendered?: string;
  cardBalance?: string;
  reloadAmount?: string;
  tipAmount?: string;
  cashbackAmount?: string;
  cashbackAmountApproved?: string;
}

export interface CardDetails {
  pan?: string;
  aid?: string;
  upc?: string;
  cardBrand?: string;
  cardCode?: string;
  cardLength?: number;
  cardName?: string;
  cardNumber?: string;
  cardType?: string;
  merchantNumber?: string;
  entryMethod?: string;
  expiryDate?: string;
  firstSix?: string;
  lastFour?: string;
  paymentTranID?: string;
  stateCode?: string;
  extendedData?: {};
}

export interface CheckDetails {
  accountNumber?: string;
  rawData: string;
  routingNumber?: string;
  checknumber?: string;
  checkType: string;
}

export interface ConfigOptions {
  companyNumber?: number;
  configAddress?: string;
  configPort?: number;
  clientKey?: string;
  secretKey?: string;
  hostAddress?: string;
  hostAddressSecondary?: string;
  hostPort?: number;
  hostPortSecondary?: number;
  laneNumber?: number;
  maxOfflineTransactionAmount?: number;
  maxOfflineTransactions?: number;
  maxTotalOfflineAmount?: number;
  promptTip?: boolean;
  storeNumber?: number;
}

export interface DeviceData {
  serialNumber?: string;
  terminalType?: string;
}

export interface PromptDetails {
  inputValue?: string;
}

export interface HostData {
  accountSecurityCodeValid?: boolean;
  hostType?: number;
  referenceID?: string;
}

export interface PaymentDetails {
  authCode?: string;
  needsSignature?: boolean;
  offline?: boolean;
  partialApproval?: boolean;
  paymentTranID?: string;
  responseCode?: string;
  responseMessage?: string;
  sessionTranID?: string;
  stan?: string;
  verificationMethod?: string;
  providerData?: string;
}

export interface ReceiptData {
  receiptLines: string[];
  receiptSection: ReceiptSection;
  receiptType: string;
}

export enum ReceiptSection {
  Header = 'Header',
  Body = 'Body',
  Footer = 'Footer',
}

export interface SignatureDetails {
  captionLine1?: string;
  captionLine2?: string;
  endTimer?: number;
  imageType?: string;
  imageData?: string;
  startTimer?: number;
}

export interface PaymentError {
  message: string;
  code: string;
}

export interface EwicEpsPrescriptionData {
  ewicData?: string;
  responseCode?: string;
  stateIdentifierCode?: string;
  pan?: string;
  authorizationCode?: string;
  referenceNumber?: string;
  enabledEpsData?: boolean;
  enabledCancel?: boolean;
  initialItemPrescriptions?: IPrescriptionItem[];
  finalItemPrescriptions?: IPrescriptionItem[];
  redemptionItems?: RedemptionItem[];
  utilizedAmountTotal?: number;
}

export interface RedemptionItem {
  ProductLineItemUniqueKey?: string;
  WicEligibility?: EwicEligibilityStatus;
  MappedProductBarcode?: string;
  ScannedProductBarcode?: string;
  IsUpc?: boolean;
  CvbSplitTenderAmount?: number;
  BarcodeCheckDigit?: string;
  UpcPluItemLen?: number;
  OrderItemWicRedemption: IPrescriptionItem[];
}
export interface EwicUtilizationData {
  redemptionItems: RedemptionItem[];
  projectedWicUtilization: IPrescriptionItem[];
  projectedEndingBalance: IPrescriptionItem[];
  rawPSRecords: string;
  utilizedAmountTotal: number;
  epsData?: EpsData;
}

export enum Status {
  Success = 'Success',
  Failure = 'Failure',
  Prompt = 'Prompt',
}

export enum EwicEligibilityStatus {
  Eligible = 'Eligible',
  Ineligible = 'Ineligible',
  Exhausted = 'Exhausted',
  ExceededPsRecordLimit = 'ExceededPsRecordLimit',
}

export enum CardDetailsContext {
  Payment = 'Payment',
  NonPayment = 'NonPayment',
}

// Utilities

export const buildBasicPaymentMessage = (): PaymentMessage => {
  return {
    clientID:
      store.getState().touchPoint.touchPointConfiguration.touchPointId || 'Emerald XLR Client',
    requestID: uuid(),
  };
};

export const buildActivateMessage = (
  externalId: string,
  price: string,
  entryMethod?: EntryMethod,
  upc?: string,
): PaymentMessage => {
  const msg: PaymentMessage = buildBasicPaymentMessage();
  const amounts: Amounts = {
    activationAmount: price,
  };
  const cardDetails: CardDetails = {
    pan: externalId,
    entryMethod,
    upc,
  };

  msg.amounts = amounts;
  msg.paymentDetails = {};
  msg.cardDetails = [cardDetails];

  return msg;
};

export const buildReloadMessage = (
  externalId: string,
  price: string,
  entryMethod?: EntryMethod,
  upc?: string,
): PaymentMessage => {
  const msg: PaymentMessage = buildBasicPaymentMessage();
  const amounts: Amounts = {
    reloadAmount: price,
  };
  const cardDetails: CardDetails = {
    pan: externalId,
    entryMethod,
    upc,
  };

  msg.amounts = amounts;
  msg.paymentDetails = {};
  msg.cardDetails = [cardDetails];

  return msg;
};

// To parse GetCardDetails info
export const parseElectronicPaymentData = (paymentMessage: PaymentMessage): EpsData => {
  try {
    const cardCollection = paymentMessage.cardDetails;
    const previousCard = cardCollection?.[(cardCollection?.length ?? 0) - 1];
    const emptyDetails: CardDetails = {
      entryMethod: '',
    };
    const ewicEpsPrescriptionData: EwicEpsPrescriptionData = {
      ewicData: paymentMessage.ewicData,
    };

    const epsData: EpsData = {
      paymentTranID: paymentMessage.paymentDetails?.paymentTranID ?? '',
      sessionTranID: paymentMessage.paymentDetails?.sessionTranID ?? '',
      card: previousCard ?? emptyDetails,
      amountDue: paymentMessage.amounts?.amountDue,
      amountApproved: paymentMessage.amounts?.amountApproved,
      referenceID: paymentMessage.hostData?.referenceID,
      authCode: paymentMessage.paymentDetails?.authCode,
      ewicEpsPrescriptionData: ewicEpsPrescriptionData,
    };
    return epsData;
  } catch (err) {
    throw new EmeraldError('Missing electronic payment data');
  }
};

export const buildConfigOptions = (): ConfigOptions => {
  const pinpadConfigDictionary = getPinPadConfig();
  const pinpadConfig = parsePinpadConfig(pinpadConfigDictionary);
  return pinpadConfig;
};

/**
 *
 * @param tendered The amount to tender for this swipe
 * @param due The total amount due for the transaction
 * @param entry Optional: The desired entry method (typically will be used for Manual)
 * @returns A Get Card Details message
 */
export const buildGCDMessage = (
  tendered: number,
  due: number,
  entry: string,
  expectedCardDetailsContext: CardDetailsContext,
): PaymentMessage => {
  const msg: PaymentMessage = buildBasicPaymentMessage();
  const cardDetail: CardDetails = {
    entryMethod: entry,
  };
  const amounts: Amounts = {
    amountDue: Math.abs(due).toString(),
    amountTendered: Math.abs(tendered).toString(),
  };
  msg.cardDetails = [cardDetail];
  msg.amounts = amounts;
  msg.expectedCardDetailsContext = expectedCardDetailsContext;
  return msg;
};

export const buildPurchaseMessage = (
  amtDue: number,
  amtTendered: number,
  payTranId: string,
  cashbackAmount?: number,
): PaymentMessage => {
  const msg: PaymentMessage = buildBasicPaymentMessage();
  msg.amounts = {
    amountTendered: amtDue.toString(),
    amountDue: amtTendered.toString(),
    cashbackAmount: cashbackAmount?.toString(),
  };
  if (hasCardData()) {
    const { card } = getCardData();
    msg.cardDetails = [card];
  }
  msg.paymentDetails = {
    paymentTranID: payTranId,
  };

  return msg;
};

export const buildReturnMessage = (
  amtDue: number,
  amtTendered: number,
  payTranId: string,
): PaymentMessage => {
  const msg: PaymentMessage = buildBasicPaymentMessage();
  msg.amounts = {
    amountTendered: amtDue.toString(),
    amountDue: amtTendered.toString(),
  };
  if (hasCardData()) {
    const { card } = getCardData();
    msg.cardDetails = [card];
  }
  msg.paymentDetails = {
    paymentTranID: payTranId,
  };

  if (msg.amounts) {
    if (msg.amounts.amountTendered) {
      msg.amounts.amountTendered = Math.abs(parseFloat(msg.amounts.amountTendered)).toString();
    }
    if (msg.amounts.amountDue) {
      msg.amounts.amountDue = Math.abs(parseFloat(msg.amounts.amountDue)).toString();
    }
  }

  return msg;
};
